import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, DollarSign, CalendarIcon, ChevronLeft, ChevronRight, X, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AmountInput } from '../components/ui/amount-input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { TruncatedText } from '../components/ui/truncated-text';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Income() {
  const { t } = useTranslation('income');
  const FULL_MONTHS = t('common:months', { returnObjects: true });
  const SHORT_MONTHS = t('common:monthsShort', { returnObjects: true });
  const { user: currentUser } = useAuth();
  const canEdit = ['admin', 'super_admin'].includes(currentUser?.role);

  const [incomes, setIncomes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Edit income state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editForm, setEditForm] = useState({ project_id: '', amount: '', currency: 'USD', month: '', year: '', income_date: '', description: '' });

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAmountUsd, setTotalAmountUsd] = useState(0);
  const [totalUzsNative, setTotalUzsNative] = useState(0);
  const [totalUsdNative, setTotalUsdNative] = useState(0);

  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');

  // Currency display toggle
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(null);

  const [formData, setFormData] = useState({
    project_id: '',
    amount: '',
    currency: 'USD',
    selectedDate: new Date(),
    periodMonth: (new Date().getMonth() + 1).toString(),
    periodYear: new Date().getFullYear().toString(),
    description: ''
  });

  // Period mode state
  const [periodMode, setPeriodMode] = useState(false);
  const [periodFrom, setPeriodFrom] = useState({ month: 1, year: new Date().getFullYear() });
  const [periodTo, setPeriodTo] = useState({ month: 12, year: new Date().getFullYear() });
  const [bulkLoading, setBulkLoading] = useState(false);

  // Fetch exchange rate
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/exchange-rates/live`, { withCredentials: true });
        if (res.data?.rate) setExchangeRate(res.data.rate);
      } catch (e) {
        console.error('Error fetching exchange rate:', e);
      }
    };
    fetchRate();
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  const fetchIncomes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      params.append('year', selectedYear);
      if (selectedMonth !== 'all') params.append('month', selectedMonth);
      if (selectedProject !== 'all') params.append('project_id', selectedProject);

      const response = await axios.get(`${API_URL}/api/incomes?${params.toString()}`, { withCredentials: true });
      setIncomes(response.data.items || []);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
      setTotalAmountUsd(response.data.total_amount_usd || 0);
      setTotalUzsNative(response.data.total_uzs_native || 0);
      setTotalUsdNative(response.data.total_usd_native || 0);
    } catch (error) {
      console.error('Error fetching incomes:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, selectedYear, selectedMonth, selectedProject, t]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchIncomes(); }, [fetchIncomes]);
  useEffect(() => { setPage(1); }, [selectedYear, selectedMonth, selectedProject, perPage]);

  const activeProjects = projects.filter(p => p.status !== 'completed');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_id) {
      toast.error(t('toast.selectProject'));
      return;
    }
    try {
      if (periodMode) {
        // Bulk create for period range
        setBulkLoading(true);
        const selectedDate = formData.selectedDate || new Date();
        const res = await axios.post(`${API_URL}/api/incomes/bulk`, {
          project_id: formData.project_id,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          from_month: periodFrom.month,
          from_year: periodFrom.year,
          to_month: periodTo.month,
          to_year: periodTo.year,
          income_date: format(selectedDate, 'yyyy-MM-dd'),
          description: formData.description || null
        }, { withCredentials: true });
        toast.success(t('toast.bulkCreated', { count: res.data.created }));
        setBulkLoading(false);
        setDialogOpen(false);
        setPeriodMode(false);
        setFormData({ project_id: '', amount: '', currency: 'USD', selectedDate: new Date(), periodMonth: (new Date().getMonth() + 1).toString(), periodYear: new Date().getFullYear().toString(), description: '' });
        fetchIncomes();
        return;
      }

      const selectedDate = formData.selectedDate || new Date();
      await axios.post(`${API_URL}/api/incomes`, {
        project_id: formData.project_id,
        amount: parseFloat(formData.amount),
        month: parseInt(formData.periodMonth),
        year: parseInt(formData.periodYear),
        currency: formData.currency,
        income_date: format(selectedDate, 'yyyy-MM-dd'),
        description: formData.description || null
      }, { withCredentials: true });

      toast.success(t('toast.added'));
      setDialogOpen(false);
      setFormData({ project_id: '', amount: '', currency: 'USD', selectedDate: new Date(), periodMonth: (new Date().getMonth() + 1).toString(), periodYear: new Date().getFullYear().toString(), description: '' });
      fetchIncomes();
    } catch (error) {
      console.error('Error adding income:', error);
      setBulkLoading(false);
      const errorMessage = error.response?.data?.detail || t('toast.addFailed');
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (incomeId) => {
    if (!window.confirm(t('toast.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/incomes/${incomeId}`, { withCredentials: true });
      toast.success(t('toast.deleted'));
      fetchIncomes();
    } catch (error) {
      console.error('Error deleting income:', error);
      const errorMessage = error.response?.data?.detail || t('toast.deleteFailed');
      toast.error(errorMessage);
    }
  };

  const openEditDialog = (income) => {
    setEditingIncome(income);
    setEditForm({
      project_id: income.project_id || '',
      amount: income.amount?.toString() || '',
      currency: income.currency || 'USD',
      month: income.month?.toString() || '',
      year: income.year?.toString() || '',
      income_date: income.income_date || '',
      description: income.description || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.project_id) {
      toast.error(t('toast.selectProject'));
      return;
    }
    try {
      await axios.patch(`${API_URL}/api/incomes/${editingIncome.income_id}`, {
        project_id: editForm.project_id,
        amount: parseFloat(editForm.amount),
        currency: editForm.currency,
        month: parseInt(editForm.month),
        year: parseInt(editForm.year),
        income_date: editForm.income_date || null,
        description: editForm.description || null,
      }, { withCredentials: true });
      toast.success(t('toast.updated'));
      setEditDialogOpen(false);
      setEditingIncome(null);
      fetchIncomes();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('toast.updateFailed');
      toast.error(errorMessage);
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project ? project.name : t('unknownProject');
  };

  const getProjectStatus = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project ? project.status : 'unknown';
  };

  // Currency display helpers
  const fmtUsd = (amount) => `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtUzs = (amount) => `${Math.round(amount || 0).toLocaleString()} UZS`;
  const fmtOriginal = (amount, currency) => currency === 'UZS' ? fmtUzs(amount) : fmtUsd(amount);

  const displayAmount = (usdAmount) => {
    if (displayCurrency === 'UZS' && exchangeRate) {
      return fmtUzs(usdAmount * exchangeRate);
    }
    return fmtUsd(usdAmount);
  };

  // Accurate total: UZS entries use original amount, USD entries convert at current rate
  const totalUzsDisplay = (displayCurrency === 'UZS' && exchangeRate)
    ? totalUzsNative + totalUsdNative * exchangeRate
    : totalAmountUsd;
  const totalUzsDisplayFmt = (displayCurrency === 'UZS' && exchangeRate)
    ? fmtUzs(totalUzsDisplay)
    : fmtUsd(totalUzsDisplay);

  const formatDate = (dateStr) => {
    if (!dateStr) return t('notAvailable');
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const hasActiveFilters = selectedMonth !== 'all' || selectedProject !== 'all';
  const clearFilters = () => { setSelectedMonth('all'); setSelectedProject('all'); };
  const startItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, totalItems);

  if (loading && incomes.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="income-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-income-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('addIncome')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.year')}</Label>
          <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
            <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.month')}</Label>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder={t('filters.allMonths')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allMonths')}</SelectItem>
              {FULL_MONTHS.map((month, idx) => (
                <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.project')}</Label>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder={t('filters.allProjects')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allProjects')}</SelectItem>
              {projects.map(project => (
                <SelectItem key={project.project_id} value={project.project_id}>
                  {project.name} {project.status === 'completed' && `(${t('projectStatus.completed')})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Currency Toggle */}
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('filters.display')}</Label>
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden h-10">
            <button
              className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'USD' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
              onClick={() => setDisplayCurrency('USD')}
            >
              $ USD
            </button>
            <button
              className={`px-3 text-sm font-medium transition-colors ${displayCurrency === 'UZS' ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
              onClick={() => setDisplayCurrency('UZS')}
            >
              UZS
            </button>
          </div>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-slate-500">
            <X className="h-4 w-4 mr-1" /> {t('filters.clear')}
          </Button>
        )}
      </div>

      {/* Summary - shows total AMOUNT, not record count */}
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="h-6 w-6" />
              <span className="text-emerald-100">{t('summary.totalIncome')}</span>
            </div>
            <div className="text-4xl font-bold">{totalUzsDisplayFmt}</div>
            <div className="text-emerald-100 mt-1">{t('summary.records', { count: totalItems, year: selectedYear })}</div>
          </div>
          {displayCurrency === 'UZS' && exchangeRate && (
            <div className="text-right text-emerald-200 text-xs">
              <div>{t('summary.rate', { rate: Number(exchangeRate).toLocaleString() })}</div>
              <div className="mt-0.5">USD: {fmtUsd(totalAmountUsd)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Income Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.project')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.period')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.description')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.original')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('table.value', { currency: displayCurrency })}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t('common:fields.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {incomes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                    {t('table.empty')}
                  </td>
                </tr>
              ) : (
                incomes.map((income) => (
                  <tr key={income.income_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {formatDate(income.income_date || income.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900 dark:text-slate-100">
                        {getProjectName(income.project_id)}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        getProjectStatus(income.project_id) === 'active'
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                          : getProjectStatus(income.project_id) === 'completed'
                          ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                          : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                      }`}>
                        {t(`projectStatus.${getProjectStatus(income.project_id)}`, { defaultValue: getProjectStatus(income.project_id) })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-full">
                        {FULL_MONTHS[income.month - 1]} {income.year}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <TruncatedText
                        text={income.description}
                        maxLength={35}
                        className="text-sm text-slate-600 dark:text-slate-400 max-w-[200px] truncate block"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {fmtOriginal(income.amount, income.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        {income.currency === 'UZS' ? fmtUzs(income.amount) : displayAmount(income.converted_amount || income.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(income)}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(income.income_id)} data-testid={`delete-income-${income.income_id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('pagination.showing', { start: startItem, end: endItem, total: totalItems })}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{t('pagination.rows')}</span>
                <Select value={perPage.toString()} onValueChange={(val) => setPerPage(parseInt(val))}>
                  <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (page <= 3) pageNum = i + 1;
                  else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = page - 2 + i;
                  return (
                    <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="sm" onClick={() => setPage(pageNum)} className="w-8 h-8 p-0">
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Income Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('form.addTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="project">{t('form.projectRequired')}</Label>
              <Select value={formData.project_id} onValueChange={(val) => setFormData({ ...formData, project_id: val })}>
                <SelectTrigger data-testid="income-project-select"><SelectValue placeholder={t('form.selectProject')} /></SelectTrigger>
                <SelectContent>
                  {activeProjects.length === 0 ? (
                    <SelectItem value="" disabled>{t('form.noActiveProjects')}</SelectItem>
                  ) : (
                    activeProjects.map(project => (
                      <SelectItem key={project.project_id} value={project.project_id}>{project.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">{t('form.projectHint')}</p>
            </div>
            <div>
              <Label htmlFor="amount">{t('form.amountRequired')}</Label>
              <AmountInput id="amount" value={formData.amount} onChange={(val) => setFormData({ ...formData, amount: val })} placeholder="1 000 000" required data-testid="income-amount-input" />
            </div>
            <div>
              <Label htmlFor="currency">{t('common:fields.currency')}</Label>
              <Select value={formData.currency} onValueChange={(val) => setFormData({ ...formData, currency: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="UZS">{t('form.uzsOption')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('form.incomeDate')} <span className="text-xs text-slate-400 font-normal">{t('form.incomeDateHint')}</span></Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.selectedDate ? format(formData.selectedDate, 'PPP') : t('form.selectDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={formData.selectedDate} onSelect={(date) => {
                    const newData = { ...formData, selectedDate: date };
                    if (date) {
                      newData.periodMonth = (date.getMonth() + 1).toString();
                      newData.periodYear = date.getFullYear().toString();
                    }
                    setFormData(newData);
                  }} />
                </PopoverContent>
              </Popover>
            </div>
            {!periodMode && (
            <div>
              <Label>{t('form.period')} <span className="text-xs text-slate-400 font-normal">{t('form.periodHint')}</span></Label>
              <div className="flex gap-2">
                <Select value={formData.periodMonth} onValueChange={(val) => setFormData({ ...formData, periodMonth: val })}>
                  <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FULL_MONTHS.map((month, idx) => (
                      <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={formData.periodYear} onValueChange={(val) => setFormData({ ...formData, periodYear: val })}>
                  <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500 mt-1">{t('form.periodNote')}</p>
            </div>
            )}

            {/* Period mode — bulk income for multiple months */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="period-mode"
                  checked={periodMode}
                  onCheckedChange={(checked) => setPeriodMode(checked)}
                />
                <Label htmlFor="period-mode" className="text-sm cursor-pointer">
                  {t('form.periodMode')}
                </Label>
              </div>

              {periodMode && (
                <div className="space-y-2 pt-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">{t('form.from')}</Label>
                      <div className="flex gap-1.5">
                        <Select value={periodFrom.month.toString()} onValueChange={(v) => setPeriodFrom(prev => ({ ...prev, month: parseInt(v) }))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FULL_MONTHS.map((m, idx) => (
                              <SelectItem key={idx} value={(idx + 1).toString()}>{SHORT_MONTHS[idx]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={periodFrom.year.toString()} onValueChange={(v) => setPeriodFrom(prev => ({ ...prev, year: parseInt(v) }))}>
                          <SelectTrigger className="h-8 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map((y) => (
                              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('form.to')}</Label>
                      <div className="flex gap-1.5">
                        <Select value={periodTo.month.toString()} onValueChange={(v) => setPeriodTo(prev => ({ ...prev, month: parseInt(v) }))}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FULL_MONTHS.map((m, idx) => (
                              <SelectItem key={idx} value={(idx + 1).toString()}>{SHORT_MONTHS[idx]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={periodTo.year.toString()} onValueChange={(v) => setPeriodTo(prev => ({ ...prev, year: parseInt(v) }))}>
                          <SelectTrigger className="h-8 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[2024, 2025, 2026, 2027].map((y) => (
                              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {(() => {
                      const fromVal = periodFrom.year * 12 + periodFrom.month;
                      const toVal = periodTo.year * 12 + periodTo.month;
                      const count = toVal - fromVal + 1;
                      return count > 0 ? t('form.periodSummary', { count, amount: formData.amount || '0', currency: formData.currency }) : t('form.invalidPeriod');
                    })()}
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="description">{t('form.descriptionOptional')}</Label>
              <Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder={t('form.descriptionPlaceholder')} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={bulkLoading} data-testid="submit-income-btn">
                {bulkLoading ? t('form.creating') : periodMode ? t('form.addMany', { count: (() => { const c = periodTo.year * 12 + periodTo.month - periodFrom.year * 12 - periodFrom.month + 1; return c > 0 ? c : 0; })() }) : t('addIncome')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common:actions.cancel')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Income Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('form.editTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-project">{t('form.projectRequired')}</Label>
              <Select value={editForm.project_id} onValueChange={(val) => setEditForm({ ...editForm, project_id: val })}>
                <SelectTrigger data-testid="edit-income-project-select"><SelectValue placeholder={t('form.selectProject')} /></SelectTrigger>
                <SelectContent>
                  {activeProjects.map(project => (
                    <SelectItem key={project.project_id} value={project.project_id}>{project.name}</SelectItem>
                  ))}
                  {/* Keep the income's current project selectable even if it's completed/paused */}
                  {editingIncome && !activeProjects.some(p => p.project_id === editingIncome.project_id) && (
                    (() => {
                      const current = projects.find(p => p.project_id === editingIncome.project_id);
                      return current ? (
                        <SelectItem key={current.project_id} value={current.project_id}>
                          {current.name} ({t(`projectStatus.${current.status}`, { defaultValue: current.status })})
                        </SelectItem>
                      ) : null;
                    })()
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <Label>{t('common:fields.amount')}</Label>
                <AmountInput
                  value={editForm.amount}
                  onChange={(val) => setEditForm({ ...editForm, amount: val })}
                  placeholder="1 000 000"
                  required
                />
              </div>
              <div>
                <Label>{t('common:fields.currency')}</Label>
                <Select value={editForm.currency} onValueChange={(val) => setEditForm({ ...editForm, currency: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UZS">UZS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common:fields.month')}</Label>
                <Select value={editForm.month} onValueChange={(val) => setEditForm({ ...editForm, month: val })}>
                  <SelectTrigger><SelectValue placeholder={t('common:fields.month')} /></SelectTrigger>
                  <SelectContent>
                    {FULL_MONTHS.map((m, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('common:fields.year')}</Label>
                <Input
                  type="number"
                  value={editForm.year}
                  onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>{t('form.dateOptional')}</Label>
              <Input
                type="date"
                value={editForm.income_date}
                onChange={(e) => setEditForm({ ...editForm, income_date: e.target.value })}
              />
            </div>
            <div>
              <Label>{t('common:fields.description')}</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder={t('form.optionalDescription')}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit">{t('form.saveChanges')}</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>{t('common:actions.cancel')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Income;
